import { toastr } from 'react-redux-toastr';
import api from '../api';
import i18n from '../../translations/i18n';

const AUTH_ROUTE = 'api/v1/Authentication';
const API_URL = process.env.REACT_APP_BACKEND_API || `https://${window.location.host}`;
const ONE_MINUTE = 1000 * 60;
const FIFTEEN_MINUTES = ONE_MINUTE * 15;
// Matches the server, which only renews the session with the HR system during its last 10 minutes.
const RENEWAL_WINDOW = ONE_MINUTE * 10;
const TOKEN_REFRESH_INTERVAL = Number(process.env.REACT_APP_JWT_REFRESH_RATE) || FIFTEEN_MINUTES;
const LAST_TOKEN_REFRESH_KEY = 'lastTokenRefresh';
const LAST_ACTIVITY_KEY = 'lastActivityAt';
let timeout;

/** @returns {number} epoch ms of the last activity recorded by any tab of this site, `0` when unknown */
const readLastActivity = () => {
    try {
        return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
    } catch {
        return 0;
    }
};

/** @returns {number} epoch seconds of this tab's session expiration, `0` when unknown */
const readKnownExpiration = () => {
    const jwtLifespan = sessionStorage.getItem('jwtLifespan');
    return jwtLifespan ? JSON.parse(jwtLifespan).Expiration || 0 : 0;
};

/**
 * @typedef {Object} AuthSession
 * @property {boolean} authenticated `false` when the cookie is missing or refused; the other properties are then absent
 * @property {string} userInfo the token's user information, as a JSON string
 * @property {number} issuedAt epoch seconds
 * @property {number} notBefore epoch seconds
 * @property {number} expiration epoch seconds
 */

class AuthService {
    /** @type {Promise<void> | null} */
    refreshInFlight = null;

    /** @type {Promise<boolean> | null} */
    pageLoadCheck = null;

    /** @type {boolean | null} the page-load check's answer once it has settled */
    pageLoadResult = null;

    /**
     * Signs in with the HR-system credentials. The API sets the HttpOnly auth cookie; the token never reaches this code.
     * Rejects when the credentials are refused, so a session the cookie may still hold from earlier is never used.
     * @param {string} username
     * @param {string} password
     */
    async login(username, password) {
        this.clearLocalSession();

        /** @type {import('axios').AxiosResponse<{ success: boolean }>} */
        const response = await api.post(`${API_URL}/${AUTH_ROUTE}/Login`, { username, password });
        if (!response.data?.success) {
            throw new Error('Sign-in refused');
        }

        await this.loadSession();
        sessionStorage.removeItem(LAST_TOKEN_REFRESH_KEY);
        return true;
    }

    /**
     * Reads the shared session once per page load, even when this tab already holds user information: the cookie may
     * have been replaced since (another tab or product signed in as someone else, or signed out). The tab's user
     * information is replaced by the session's, or cleared when there is none. Every caller of the same page load
     * (several routes, React StrictMode running effects twice) shares the one request.
     * When the request itself fails, the tab keeps the user information it already had.
     * @returns {Promise<boolean>} `true` when the tab holds a session
     */
    checkSessionOnPageLoad() {
        if (!this.pageLoadCheck) {
            this.pageLoadCheck = api.get(`${API_URL}/${AUTH_ROUTE}/Session`)
                .then((response) => {
                    if (response.data?.authenticated) {
                        this.setUserInfo(response.data);
                        return true;
                    }
                    this.clearLocalSession();
                    return false;
                })
                .catch(() => this.isLoggedIn())
                .then((isAuthenticated) => {
                    this.pageLoadResult = isAuthenticated;
                    return isAuthenticated;
                });
        }
        return this.pageLoadCheck;
    }

    /** Reads the session behind the HttpOnly auth cookie; rejects when the cookie is missing or refused. */
    async loadSession() {
        /** @type {import('axios').AxiosResponse<AuthSession>} */
        const response = await api.get(`${API_URL}/${AUTH_ROUTE}/Session`);
        if (!response.data?.authenticated) {
            throw new Error('No authenticated session');
        }
        this.setUserInfo(response.data);
    }

    /**
     * Checked every minute: renews the session when the refresh rate has elapsed, or when the session is inside the
     * server's renewal window (without the second condition a 15-minute rate would skip that whole window).
     */
    refreshToken() {
        clearTimeout(timeout);
        timeout = setTimeout(this.refreshToken.bind(this), ONE_MINUTE);

        if (!sessionStorage.getItem('jwtUserInfo') || this.refreshInFlight) {
            return;
        }

        const now = Date.now();
        const lastRefresh = sessionStorage.getItem(LAST_TOKEN_REFRESH_KEY);
        if (!lastRefresh) {
            sessionStorage.setItem(LAST_TOKEN_REFRESH_KEY, new Date(now).toISOString());
        }

        const isRateElapsed = !!lastRefresh && now - new Date(lastRefresh).getTime() >= TOKEN_REFRESH_INTERVAL;
        const isInRenewalWindow = readKnownExpiration() * 1000 - now < RENEWAL_WINDOW;
        if (!isRateElapsed && !isInRenewalWindow) {
            return;
        }

        sessionStorage.setItem(LAST_TOKEN_REFRESH_KEY, new Date(now).toISOString());
        this.refreshInFlight = api.post(`${API_URL}/${AUTH_ROUTE}/KeepSessionAlive`)
            .then((response) => {
                if (!response.data?.success) {
                    throw new Error('Session could not be renewed');
                }

                // Reloaded even when this call did not renew it: another tab or product may have.
                return this.loadSession();
            })
            .catch(async () => {
                if (await this.keepSessionIfStillValid()) {
                    return;
                }

                toastr.error('Error', i18n.t('common:Login.genericError'));
                this.clearLocalSession();
                window.location.reload();
            })
            .finally(() => {
                this.refreshInFlight = null;
            });
    }

    startTokenRefreshInterval() {
        sessionStorage.removeItem(LAST_TOKEN_REFRESH_KEY);
        clearTimeout(timeout);
        timeout = setTimeout(this.refreshToken.bind(this), ONE_MINUTE);
    }

    /**
     * A failed renewal does not mean the session is gone: another tab or product may have renewed it at the same
     * moment, consuming the HR system's refresh token this call then failed on. The shared session is read back
     * and kept while it is still valid.
     * @returns {Promise<boolean>} `true` when the session is still valid and this tab was brought up to date
     */
    async keepSessionIfStillValid() {
        try {
            await this.loadSession();
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Records user activity for every tab of this site, so an idle tab can tell the session is still in use.
     * @returns {number} the recorded time, epoch ms
     */
    markActivity() {
        const now = Date.now();
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
        } catch {
            // Without storage, activity in other tabs is simply not seen.
        }
        return now;
    }

    /**
     * Called before an inactivity sign-out. The auth cookie is shared by every tab and every Zaddons product on the
     * domain, so signing out ends the session for all of them. The session is still in use, and this tab is brought
     * up to date, when another tab of this site was active after `idleSince` or the session was renewed since this
     * tab last read it.
     * @param {number} idleSince epoch ms of this tab's last activity
     * @returns {Promise<boolean>} `true` when the session must be kept
     */
    async isSessionActiveElsewhere(idleSince) {
        const knownExpiration = readKnownExpiration();

        try {
            /** @type {import('axios').AxiosResponse<AuthSession>} */
            const response = await api.get(`${API_URL}/${AUTH_ROUTE}/Session`);
            const session = response.data;
            if (session?.authenticated && (readLastActivity() > idleSince || session.expiration > knownExpiration)) {
                this.setUserInfo(session);
                return true;
            }
        } catch {
            // An unreachable session is treated as ended.
        }

        return false;
    }

    /**
     * Re-reads the shared session for a tab that stayed open: another tab or product may have signed in as someone
     * else, or signed out, since this page loaded. The same user only refreshes this tab's lifespan.
     * When the Session request fails the tab is left as it is.
     * @returns {Promise<boolean>} `false` when the session ended or now belongs to another user
     */
    async isSessionStillForThisUser() {
        let session;
        try {
            /** @type {import('axios').AxiosResponse<AuthSession>} */
            const response = await api.get(`${API_URL}/${AUTH_ROUTE}/Session`);
            session = response.data;
        } catch {
            return true;
        }

        if (!session?.authenticated) {
            return false;
        }

        const tabUserId = JSON.parse(sessionStorage.getItem('jwtUserInfo') || '{}')?.UserId;
        if (JSON.parse(session.userInfo || '{}')?.UserId !== tabUserId) {
            return false;
        }

        sessionStorage.setItem('jwtLifespan', JSON.stringify({
            IssuedAt: session.issuedAt,
            NotBefore: session.notBefore,
            Expiration: session.expiration,
        }));
        return true;
    }

    /**
     * @param {CommonClient2.JwtLicenses} license
     * @returns {boolean}
     */
    validateLicense = (license) => {
        const jwtUserInfo = sessionStorage.getItem('jwtUserInfo');
        if (jwtUserInfo) {
            /** @type {CommonClient2.IUserInfo} */
            const userInfo = JSON.parse(jwtUserInfo);
            return userInfo.Licenses.includes(license);
        }
        return false;
    }

    /**
     * Keeps the user information the UI needs; the token itself stays in the HttpOnly cookie.
     * @param {AuthSession} session
     */
    setUserInfo(session) {
        const tokenInfo = {
            IssuedAt: session.issuedAt,
            NotBefore: session.notBefore,
            Expiration: session.expiration,
        };

        sessionStorage.removeItem('authToken');
        sessionStorage.setItem('jwtUserInfo', session.userInfo);
        sessionStorage.setItem('jwtLifespan', JSON.stringify(tokenInfo));
    }

    /**
     * Ends the shared session (this signs the browser out of every Zaddons product on the domain), then redirects
     * whether or not the API could be reached.
     * @param {string=} redirectUrl
     */
    logout(redirectUrl) {
        this.clearLocalSession();

        api.post(`${API_URL}/${AUTH_ROUTE}/ClearCookies`)
            .catch(() => { })
            .finally(() => {
                window.location.href = redirectUrl || '';
            });
    }

    /** Clears this tab only; the shared cookie is left to the other tabs and products. */
    clearLocalSession() {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('jwtUserInfo');
        sessionStorage.removeItem('jwtLifespan');
        sessionStorage.removeItem('userInitials');
    }

    /**
     * A lifespan past its expiration only clears this tab: the shared session may have been renewed by another tab
     * or product, and the next bootstrap reads it back from the cookie.
     */
    isLoggedIn() {
        if (!sessionStorage.getItem('jwtUserInfo')) {
            return false;
        }

        if (Date.now() > readKnownExpiration() * 1000) {
            this.clearLocalSession();
            return false;
        }

        return true;
    }
}

export default (new AuthService());

export const getAuthHeader = () => ({
    AppKey: process.env.REACT_APP_APP_KEY,
});

/**
 *  Get currently logged in User's information
 * @param {string[]?=} attributes *optionnal* Array of attributes wanted, default = all properties
 * @returns {*} object with selected params
 */
export const getCurrentUserInfoParams = (attributes = null) => {
    const data = JSON.parse(sessionStorage.getItem('jwtUserInfo') || '');

    if (!data) {
        return {};
    }

    if (attributes == null) {
        return data;
    }

    const info = {};
    attributes.forEach((a) => {
        if (!data[a]) {
            return;
        }
        info[a] = data[a];
    });
    return info;
};
